import { config } from '../config.ts';

export interface TerrainSampler {
  sampleHeight(worldX: number, worldZ: number): number;
}

export async function createTerrainSampler(
  device: GPUDevice,
  heightmap: GPUTexture,
): Promise<TerrainSampler> {
  const w = heightmap.width;
  const h = heightmap.height;
  const bpr = Math.ceil((w * 4) / 256) * 256;
  const rb = device.createBuffer({
    label: 'terrain-sampler.readback',
    size: bpr * h,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });
  const enc = device.createCommandEncoder({ label: 'terrain-sampler.encoder' });
  enc.copyTextureToBuffer(
    { texture: heightmap },
    { buffer: rb, bytesPerRow: bpr },
    { width: w, height: h, depthOrArrayLayers: 1 },
  );
  device.queue.submit([enc.finish()]);
  await rb.mapAsync(GPUMapMode.READ);
  const raw = new Float32Array(rb.getMappedRange().slice(0));
  rb.unmap();
  rb.destroy();

  const stride = bpr / 4;
  const data = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      data[y * w + x] = raw[y * stride + x] ?? 0;
    }
  }

  const half = config.worldSize * 0.5;
  const worldHeight = config.worldHeight;

  const sampleHeight = (worldX: number, worldZ: number): number => {
    const u = (worldX + half) / config.worldSize;
    const v = (worldZ + half) / config.worldSize;
    if (u < 0 || u > 1 || v < 0 || v > 1) return 0;

    const fx = u * (w - 1);
    const fy = v * (h - 1);
    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const x1 = Math.min(x0 + 1, w - 1);
    const y1 = Math.min(y0 + 1, h - 1);
    const tx = fx - x0;
    const ty = fy - y0;

    const h00 = data[y0 * w + x0] ?? 0;
    const h10 = data[y0 * w + x1] ?? 0;
    const h01 = data[y1 * w + x0] ?? 0;
    const h11 = data[y1 * w + x1] ?? 0;

    const a = h00 + (h10 - h00) * tx;
    const b = h01 + (h11 - h01) * tx;
    const sample = a + (b - a) * ty;
    return sample * worldHeight;
  };

  return { sampleHeight };
}
